import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { PAGE_OUTER_PADDING_TOP } from '../constants/housieLiveStyles';
import AppFooter from './base/AppFooter';
import LobbyJoinedPlayerRow from './LobbyJoinedPlayerRow';
import './RoomDetailLobby.css';

const APP_LINK_URL = 'https://onelink.to/m6xbyw';
const DEFAULT_WAITING_VIDEO = IMAGE_CONSTANTS.V2_WAITING_SCREEN_VIDEO;
const CLOUDFLARE_STREAM_ID_RE = /cloudflarestream\.com\/([a-f0-9]+)/i;

const isFileLike = (value) =>
  (typeof Blob !== 'undefined' && value instanceof Blob) ||
  (typeof File !== 'undefined' && value instanceof File);

const pickMediaUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (isFileLike(value)) return '';
  if (typeof value === 'object') {
    const nested =
      value.roomImage ||
      value.roomImageUrl ||
      value.waitingMediaUrl ||
      value.waitingMedia ||
      value.mediaUrl ||
      value.imageUrl;
    if (nested && nested !== value) return pickMediaUrl(nested);
    if (
      typeof value.url === 'string' &&
      !value.roomName &&
      !value.gameId &&
      !value.contestId
    ) {
      return value.url.trim();
    }
  }
  return '';
};

export const resolveWaitingMediaUrl = (...sources) => {
  for (const source of sources) {
    const url = pickMediaUrl(source);
    if (url) return url;
  }
  return '';
};

const isVideoUrl = (url = '') =>
  /\.(mp4|webm|ogg|mov|m3u8)(\?|$)/i.test(url) || /\/video\//i.test(url);
const getCloudflareStreamId = (url = '') => {
  const match = String(url).match(CLOUDFLARE_STREAM_ID_RE);
  return match ? match[1] : null;
};
const getCloudflareStreamEmbedSrc = (url = '') => {
  const videoId = getCloudflareStreamId(url);
  if (!videoId) return url;
  return `https://iframe.cloudflarestream.com/${videoId}?autoplay=true&muted=true&loop=true&controls=false&preload=true`;
};

const RoomDetailLobby = ({
  roomName,
  roomCode,
  members = [],
  mediaUrl,
  mediaFile,
  onBack,
  onStartGame,
  onCopyCode,
  startDisabled = false,
  copyright = TEXT_LABELS.COPY_RIGHT_TEXT_LABEL,
}) => {
  const [qrExpanded, setQrExpanded] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [objectUrl, setObjectUrl] = useState('');
  const waitingVideoRef = useRef(null);

  useEffect(() => {
    if (mediaUrl || !(mediaFile instanceof Blob)) {
      setObjectUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(mediaFile);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaUrl, mediaFile]);

  const customMediaUrl = mediaUrl || objectUrl;
  const resolvedMediaUrl = customMediaUrl || DEFAULT_WAITING_VIDEO;
  const isDefaultWaitingVideo = !customMediaUrl;
  const streamEmbedSrc = getCloudflareStreamId(resolvedMediaUrl)
    ? getCloudflareStreamEmbedSrc(resolvedMediaUrl)
    : null;
  const qrSrc =
    IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_QR ||
    IMAGE_CONSTANTS.V2_BARCODE ||
    IMAGE_CONSTANTS.V2_REVAMPED_QR_CODE;
  const logoSrc =
    IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_RAPCHIK_LOGO || IMAGE_CONSTANTS.V2_HEADER_LOGO;

  const mediaIsVideo = streamEmbedSrc
    ? false
    : isDefaultWaitingVideo
      ? true
      : mediaUrl
        ? isVideoUrl(mediaUrl)
        : Boolean(mediaFile && String(mediaFile.type || '').startsWith('video/')) ||
          isVideoUrl(resolvedMediaUrl);

  useEffect(() => {
    if (!mediaIsVideo || !waitingVideoRef.current) return undefined;
    const video = waitingVideoRef.current;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* Autoplay may be blocked until user gesture; muted + playsInline covers most cases */
      });
    }
    return undefined;
  }, [mediaIsVideo, resolvedMediaUrl]);

  return (
    <div
      className="room-detail"
      style={{
        paddingTop: PAGE_OUTER_PADDING_TOP,
        backgroundColor: 'transparent',
      }}
    >
      <header className="room-detail__header">
        <button type="button" className="room-detail__back" onClick={onBack}>
          <span className="room-detail__back-icon" aria-hidden="true">
            <img src={IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_BACK_ARROW} alt="" />
          </span>
          <span className="room-detail__back-label">Back to Previous Page</span>
        </button>
        <h1 className="room-detail__room-name">{roomName || 'ROOM NAME'}</h1>
        <img className="room-detail__logo" src={logoSrc} alt="Rapchik" />
      </header>

      <div className="room-detail__body">
        <div className="room-detail__main">
          <section className="room-detail__media" aria-label="Waiting screen media">
            {streamEmbedSrc ? (
              <iframe
                className="room-detail__media-content room-detail__media-content--waiting room-detail__media-content--stream"
                src={streamEmbedSrc}
                title="Waiting screen"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
              />
            ) : mediaIsVideo ? (
              <video
                ref={waitingVideoRef}
                className={`room-detail__media-content${isDefaultWaitingVideo ? ' room-detail__media-content--waiting' : ''}`}
                src={resolvedMediaUrl}
                controls={!isDefaultWaitingVideo}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              />
            ) : (
              <img
                className="room-detail__media-content"
                src={resolvedMediaUrl}
                alt="Waiting screen"
              />
            )}
          </section>

          <div className="room-detail__cards">
            {/* Figma 17981:21272 / 21274 / 21277 / 21279 / 18011:21342 — Scan to Join */}
            <article className="room-detail__promo-card room-detail__promo-card--scan">
              <div className="room-detail__qr-frame">
                <img
                  src={qrSrc}
                  alt="Scan to join QR code"
                  width={154}
                  height={154}
                />
              </div>
              <div className="room-detail__promo-copy">
                <div className="room-detail__promo-text">
                  <h2 className="room-detail__card-title">Scan to Join</h2>
                  <p className="room-detail__card-text">
                    Get Scan this QR code to join the room.
                  </p>
                </div>
                <button
                  type="button"
                  className="room-detail__pill-link"
                  onClick={() => setQrExpanded(true)}
                >
                  <span>Expand View</span>
                  <img
                    className="room-detail__pill-chevron"
                    src={IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_CHEVRON}
                    alt=""
                    width={20}
                    height={20}
                  />
                </button>
              </div>
            </article>

            {/* Figma 18011:21350 / 21355 / 21356 / 21357 / 21365 — Room / join code */}
            <article className="room-detail__promo-card room-detail__promo-card--code">
              <button
                type="button"
                className="room-detail__code-box"
                onClick={onCopyCode}
                aria-label="Copy room code"
              >
                <span className="room-detail__code-inner">
                  <span className="room-detail__code-value">{roomCode || '-----'}</span>
                  <span className="room-detail__code-copy">
                    <img src={IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_COPY} alt="" width={26} height={26} />
                    <span>Copy Code</span>
                  </span>
                </span>
              </button>
              <div className="room-detail__promo-copy">
                <div className="room-detail__promo-text">
                  <h2 className="room-detail__card-title">Room Code</h2>
                  <p className="room-detail__card-text">
                    Share this code to let members join
                    <br />
                    the room.
                  </p>
                </div>
                <button
                  type="button"
                  className="room-detail__pill-link"
                  onClick={() => setHowItWorksOpen(true)}
                >
                  <span>How It Works?</span>
                  <img
                    className="room-detail__pill-chevron"
                    src={IMAGE_CONSTANTS.V2_REVAMPED_LOBBY_CHEVRON}
                    alt=""
                    width={20}
                    height={20}
                  />
                </button>
              </div>
            </article>
          </div>
        </div>

        {/* Figma 18011:21383 — Members + Start */}
        <aside className="room-detail__sidebar" aria-label="Members">
          <div className="room-detail__sidebar-body">
            <h2 className="room-detail__members-title">
              {`TOTAL MEMBERS (${members?.length || 0})`}
            </h2>

            <div className="room-detail__members-list">
              {members?.length > 0 ? (
                members.map((player, index) => (
                  <LobbyJoinedPlayerRow
                    key={player?.userId || player?.id || `${player?.username || 'user'}-${index}`}
                    username={player?.username || 'Player'}
                    avatar={player?.avatar || IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER}
                  />
                ))
              ) : (
                <div className="room-detail__empty">
                  <strong>{TEXT_LABELS.RAP_HOUSIE_ROOM_GAME_CODE_SHARE_NO_PLAYER_TITLE}</strong>
                  <span>{TEXT_LABELS.RAP_HOUSIE_ROOM_GAME_CODE_SHARE_NO_PLAYER_SUBTITLE}</span>
                </div>
              )}
            </div>
          </div>

          <div className="room-detail__sidebar-footer">
            <p className="room-detail__hint">
              {TEXT_LABELS.RAP_HOUSIE_ROOM_GAME_CODE_SHARE_HINT ||
                'You can start the game or wait for others to join'}
            </p>
            <div className="room-detail__start-wrap">
              <div className="room-detail__start-shadow" aria-hidden="true" />
              <button
                type="button"
                className="room-detail__start"
                onClick={onStartGame}
                disabled={startDisabled}
              >
                {TEXT_LABELS.RAP_HOUSIE_START_GAME_BUTTON_LABEL}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <AppFooter text={copyright} />

      {qrExpanded ? (
        <div
          className="room-detail__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded QR code"
          onClick={() => setQrExpanded(false)}
        >
          <div className="room-detail__overlay-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="room-detail__overlay-close"
              aria-label="Close"
              onClick={() => setQrExpanded(false)}
            >
              ×
            </button>
            <h3>Scan to Join</h3>
            <p>
              Scan this QR code or open{' '}
              <a href={APP_LINK_URL} target="_blank" rel="noopener noreferrer">
                {APP_LINK_URL}
              </a>{' '}
              to get the app and join the room.
            </p>
            <img src={qrSrc} alt="Expanded room QR code" />
          </div>
        </div>
      ) : null}

      {howItWorksOpen ? (
        <div
          className="room-detail__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="How it works"
          onClick={() => setHowItWorksOpen(false)}
        >
          <div className="room-detail__overlay-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="room-detail__overlay-close"
              aria-label="Close"
              onClick={() => setHowItWorksOpen(false)}
            >
              ×
            </button>
            <h3>How It Works?</h3>
            <p>
              Share the room code with your members, or ask them to scan the QR code. Once they
              join from the Rapchik app, they will appear in the members list. Start the game when
              everyone is ready.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

RoomDetailLobby.propTypes = {
  roomName: PropTypes.string,
  roomCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  members: PropTypes.array,
  mediaUrl: PropTypes.string,
  mediaFile: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Blob)]),
  onBack: PropTypes.func,
  onStartGame: PropTypes.func,
  onCopyCode: PropTypes.func,
  startDisabled: PropTypes.bool,
  copyright: PropTypes.string,
};

export default RoomDetailLobby;
